import { useTranslation } from 'react-i18next';
import { usePlans } from '../../../../api/hooks/usePlans';
import { useCreditsBundle } from '../../../../api/hooks/sales/useCreditsBundle';
import { dateAsYMD } from '../../../../config/utils/dateHelpers';
import { STANDALONE_FEATURE_LABEL, StandaloneFeatureType } from '../../../../interfaces/enumerables';
import { selectSelectedCompany } from '../../../../store/companies/companiesSlice';
import { useAppSelector } from '../../../../store/hooks';

export const SubscriptionSection = () => {
  const { t } = useTranslation();
  const selectedCompany = useAppSelector(selectSelectedCompany);
  const { data: plansData } = usePlans(selectedCompany?.vatNumber || '');
  const { data: credits, isLoading: isLoadingCredits } = useCreditsBundle();

  const planEntries = plansData?.plans || [];
  const featureEntries = plansData?.features || [];
  const hasCredits = !isLoadingCredits && credits && credits.length > 0;

  if (!planEntries.length && !featureEntries.length && !hasCredits) return null;

  const planExpiry = planEntries.length
    ? planEntries.reduce((max, p) => (p.validUntil > max ? p.validUntil : max), planEntries[0].validUntil)
    : null;

  const featureExpiryMap: Partial<Record<StandaloneFeatureType, string>> = {};
  for (const fe of featureEntries) {
    const current = featureExpiryMap[fe.feature];
    if (!current || fe.validUntil > current) {
      featureExpiryMap[fe.feature] = fe.validUntil;
    }
  }

  const hasFeatures = Object.keys(featureExpiryMap).length > 0;

  return (
    <div className="margin-t-20 flex-start-start-column-nowrap">
      <div className="padding-b-16 boldText6">{t('SETTINGS.SUBSCRIPTION_SECTION')}</div>
      <div className="flex-start-start-column-nowrap gap-10 border-1-rad-10 padding-10 width100">
        {planExpiry && (
          <div className="normalText5">
            {t('SETTINGS.SUBSCRIPTION')} - {t('SETTINGS.EXPIRATION_DATE')}: <b>{dateAsYMD(planExpiry)}</b>
          </div>
        )}
        {hasFeatures && (
          <>
            <div className="boldText5">{t('SETTINGS.FEATURES_SECTION')}</div>
            {Object.entries(featureExpiryMap).map(([featureId, expiry], index) => (
              <div key={featureId} className="normalText5">
                {index + 1}. {t(STANDALONE_FEATURE_LABEL[featureId] ?? featureId)} - {t('SETTINGS.EXPIRATION_DATE')}:{' '}
                <b>{dateAsYMD(expiry)}</b>
              </div>
            ))}
          </>
        )}
        {hasCredits && (
          <>
            <div className="boldText5">{t('SETTINGS.PROVIDER_CREDITS_HEADER')}</div>
            {credits.map((bundle, index) => (
              <div key={index} className="normalText5">
                <b>{index + 1}.</b> {t('SETTINGS.INITIAL_QUANTITY')}: <b>{bundle.original_quantity}</b> -{' '}
                {t('SETTINGS.REMAINING_QUANTITY')}: <b>{bundle.remainingQuantity}</b> -{' '}
                {t('SETTINGS.EXPIRATION_DATE')}: <b>{dateAsYMD(bundle?.expiresOn)}</b>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
